import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "heavy-duty-towing",
    n: "01",
    title: "Heavy-Duty Truck Towing",
    desc: "Semi-truck, tractor-trailer, box truck, RV, and motorhome recovery with heavy boom winches.",
    badge: "24/7 Heavy Recovery",
    img: "/images/fleet-1.webp",
  },
  {
    id: "roadside-repair",
    n: "02",
    title: "Heavy Truck & Trailer Repair",
    desc: "Mobile mechanics for heavy trucks and trailers — diesel repair, air lines, tires, brakes, and jumpstarts wherever you sit.",
    badge: "Mobile Mechanics",
    img: "/images/fleet-4.webp",
  },
  {
    id: "car-towing",
    n: "03",
    title: "Light & Medium Car Towing",
    desc: "Flatbed and wheel-lift towing for sedans, SUVs, pickups, and luxury vehicles — loaded damage-free.",
    badge: "Damage-Free Flatbed",
    img: "/images/fleet-3.webp",
  },
  {
    id: "car-lockouts",
    n: "04",
    title: "Lockouts & Fuel Delivery",
    desc: "Fast lockout response, emergency diesel and gasoline delivery, and key recovery when you're stranded.",
    badge: "Rapid Arrival",
    img: "/images/fleet-2.webp",
  },
  {
    id: "equipment-hauling",
    n: "05",
    title: "Equipment Hauling",
    desc: "Tractors, machinery, and equipment of all sizes moved on our Landoll and 55-ton lowboy trailers.",
    badge: "Landoll & 55-Ton Lowboy",
    img: "/images/fleet-6.png",
  },
  {
    id: "junk-cars",
    n: "06",
    title: "We Buy Junk Cars & Equipment",
    desc: "Turn that dead car, old machine, or rusty equipment into cash — fair prices, we haul it away ourselves.",
    badge: "Cash for Clunkers",
    img: "https://images.unsplash.com/photo-1645216755684-c946ce202427?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    href: "#sell",
  },
  {
    id: "demolition",
    n: "07",
    title: "Building Demolition",
    desc: "A special service few can offer — safe, professional building demolition with our own heavy equipment.",
    badge: "Special Service",
    img: "https://images.unsplash.com/photo-1616551569669-b60598758c4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-surface py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember" data-testid="services-overline">
              What We Haul
            </p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="services-heading">
              Services Built<br />for the Road
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-400">
            From a locked sedan at midnight to a loaded semi on the berm — one number covers it all.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.id}
              href={s.href || "#request"}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`service-card-${s.id}`}
              className="group relative flex flex-col border border-white/10 bg-steel transition-colors duration-300 hover:border-ember/60"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-steel via-transparent to-transparent" />
                <span className="absolute left-4 top-4 bg-ink/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ember backdrop-blur-sm">
                  {s.badge}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="font-mono text-xs text-slate-500">{s.n}</span>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-white">
                  {s.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ember opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Get this service <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </motion.a>
          ))}
          <motion.a
            href="tel:+13302014612"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-testid="service-card-call-dispatch"
            className="group flex min-h-[280px] flex-col items-start justify-between border border-ember/40 bg-ember/10 p-6 transition-colors duration-300 hover:bg-ember"
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember transition-colors duration-300 group-hover:text-white/80">Something Else?</span>
            <div>
              <h3 className="font-display text-3xl font-black uppercase leading-tight text-white">
                If It Rolls,<br />We Haul It.
              </h3>
              <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ember transition-colors duration-300 group-hover:text-white">
                Call dispatch — (330) 201-4612 <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
